/**
 * 合作内容「属性集」数据管道 —— 表格模式与折叠面板模式共用的转换/更新工具。
 *
 * 统一在「DemandCooperatePropertyValueDto[]」与「{ [property_code]: value } map」两层间往返，
 * 级联（getLogicOptions）与条件必填（getRequiredRule）都以 map 作为整行输入。
 * 语义与 CooperationPropertyForm 内既有实现保持一致（此处仅下沉复用，不改行为）。
 */
import type {
  DemandCooperatePropertyValueDto,
  DemandCooperatePropertiesVo,
  TemplateStandardItemDto,
} from '@mpc/common/src/services/api';

/** property_values 数组 → { [property_code]: value } map。非多值属性长度<=1 归为单值（与现有一致）。 */
export const cooperationValueToFormValue = (
  propertyValues: DemandCooperatePropertyValueDto[] = [],
): Record<string, any> =>
  propertyValues.reduce<Record<string, any>>((map, property) => {
    map[property.property_code] =
      property.value?.length > 1 ? property.value : property.value?.[0];
    return map;
  }, {});

/** form value map → property_values（含 standard_schema(规范 type=1) + property_schema(动态 type=0) 合并）。 */
export const formValueToCooperationValue = (
  formValue: Record<string, any>,
  schema: DemandCooperatePropertiesVo,
): DemandCooperatePropertyValueDto[] => {
  const standardValues =
    schema?.standards?.map?.((schemaItem) => {
      const value = formValue?.[schemaItem.code] ?? [];
      return {
        property_code: schemaItem.code,
        standard_schema: schemaItem,
        value: Array.isArray(value) ? value : [value],
        type: 1,
      } as DemandCooperatePropertyValueDto;
    }) || [];
  const propertyValues =
    schema?.properties?.map?.((schemaItem) => {
      const value = formValue?.[schemaItem.property_code] ?? [];
      return {
        property_code: schemaItem.property_code,
        property_schema: schemaItem,
        value: Array.isArray(value) ? value : [value],
        type: 0,
      } as DemandCooperatePropertyValueDto;
    }) || [];
  return standardValues.concat(propertyValues);
};

/** 对齐 schema：为每条已有的 property_values 补全缺失字段（保证列渲染绝不 undefined）。 */
export const alignPropertyValues = (
  propertyValues: DemandCooperatePropertyValueDto[],
  schema: DemandCooperatePropertiesVo,
): DemandCooperatePropertyValueDto[] => {
  const standards =
    schema?.standards?.map((schemaItem) => {
      const standard =
        propertyValues.find((n) => n.property_code === schemaItem.code) || {};
      return {
        value: [],
        property_code: schemaItem.code,
        standard_schema: schemaItem,
        label: schemaItem.label,
        type: 1,
        ...standard,
      } as DemandCooperatePropertyValueDto;
    }) || [];
  const properties =
    schema?.properties?.map((schemaItem) => {
      const property =
        propertyValues.find((n) => n.property_code === schemaItem.property_code) || {};
      return {
        value: [],
        property_code: schemaItem.property_code,
        property_schema: schemaItem,
        type: 0,
        label: schemaItem.property_name,
        ...property,
      } as DemandCooperatePropertyValueDto;
    }) || [];
  return standards.concat(properties);
};

/** 表格行内单属性写回：不可变更新 row.property_values[code]，返回新 row。 */
export const updateRowProperty = (
  row: Record<string, any>,
  code: string,
  value: any,
  schema: DemandCooperatePropertiesVo,
): Record<string, any> => {
  const map = cooperationValueToFormValue(row.property_values);
  map[code] = value;
  const nextPropertyValues = formValueToCooperationValue(map, schema);
  return {
    ...row,
    property_values: nextPropertyValues,
  };
};

/**
 * property_type → 表格列控件类型映射（决定 custom 列渲染哪种控件）。
 * ● dropdown_radio / dropdown_list：Select（单选/多选）
 * ● input：Input
 * ● text：TextArea
 * ● numeric：InputNumber
 * ● date_input / date_range：DatePicker（range 用于区间）
 */
export const toPropertyControlType = (
  propertyType?: string,
): 'select' | 'input' | 'textarea' | 'numeric' | 'date' | 'dateRange' | 'unknown' => {
  switch (propertyType) {
    case 'dropdown_radio':
    case 'dropdown_list':
      return 'select';
    case 'input':
      return 'input';
    case 'text':
      return 'textarea';
    case 'numeric':
      return 'numeric';
    case 'date_input':
      return 'date';
    case 'date_range':
      return 'dateRange';
    default:
      return 'unknown';
  }
};

/** 标准属性（standardCode*）的 code 取 schemaItem.code，动态属性取 property_code。供列定义统一取值。 */
export const getPropertyCode = (
  schemaItem: TemplateStandardItemDto | any,
): string => schemaItem.code ?? schemaItem.property_code;
